import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { db, auth } from '../firebase';
import { collection, query, orderBy, onSnapshot, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { signOut, onAuthStateChanged } from 'firebase/auth';
import './AdminDashboard.css';

const SESSION_TIMEOUT_MS = 30 * 60 * 1000;


const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.4, ease: [0.25, 0.1, 0.25, 1] } },
  exit: { opacity: 0, y: -8, transition: { duration: 0.2 } }
};

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.06, delayChildren: 0.1 } }
};

//technician editing
const TECHNICIANS = [
  'Jorge Reyes',
  'Technician 2',
  'Technician 3',
];

const STATUS_OPTIONS = [
  { value: 'new', label: 'New' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'approved', label: 'Approved' },
  { value: 'dropped', label: 'Dropped' },
  { value: 'completed', label: 'Completed' },
];

const FILTER_OPTIONS = ['all', 'new', 'contacted', 'approved', 'dropped', 'completed'];

const AdminDashboard = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Auth guard: redirect if not authenticated
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        window.location.href = '/admin';
      }
    });
    return () => unsubscribe();
  }, []);

  // Session inactivity timeout — auto logout after 30 min of no interaction
  useEffect(() => {
    let timeout;
    const resetTimer = () => {
      clearTimeout(timeout);
      timeout = setTimeout(async () => {
        await signOut(auth);
      }, SESSION_TIMEOUT_MS);
    };
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(e => window.addEventListener(e, resetTimer));
    resetTimer();
    return () => {
      clearTimeout(timeout);
      events.forEach(e => window.removeEventListener(e, resetTimer));
    };
  }, []);

  useEffect(() => {
    const q = query(collection(db, 'requests'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const requestsData = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setRequests(requestsData);
      setLoading(false);
    }, (error) => {
      console.error('Error fetching requests:', error);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const handleLogout = async () => {
    try { await signOut(auth); } catch (e) { console.error('Logout error:', e); }
  };

  const updateField = async (id, field, value) => {
    try { await updateDoc(doc(db, 'requests', id), { [field]: value }); }
    catch (e) { console.error(`Error updating ${field}:`, e); }
  };

  const updateStatus = async (id, newStatus) => {
    const updates = { status: newStatus };
    // Clear estimated completion if moving away from approved
    if (newStatus !== 'approved') {
      updates.estimatedCompletion = null;
    }
    try { await updateDoc(doc(db, 'requests', id), updates); }
    catch (e) { console.error('Error updating status:', e); }
  };

  const deleteRequest = async (id) => {
    if (window.confirm('Delete this request?')) {
      try { await deleteDoc(doc(db, 'requests', id)); }
      catch (e) { console.error('Error deleting request:', e); }
    }
  };

  // Derived data
  const analytics = useMemo(() => {
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekStart = new Date(todayStart);
    weekStart.setDate(weekStart.getDate() - 7);

    const today = requests.filter(r => r.createdAt?.toDate() >= todayStart).length;
    const thisWeek = requests.filter(r => r.createdAt?.toDate() >= weekStart).length;
    const total = requests.length;
    const newCount = requests.filter(r => r.status === 'new').length;
    const contacted = requests.filter(r => r.status === 'contacted').length;
    const approved = requests.filter(r => r.status === 'approved').length;
    const dropped = requests.filter(r => r.status === 'dropped').length;
    const completed = requests.filter(r => r.status === 'completed').length;
    const pending = newCount + contacted + approved;

    // Service breakdown
    const serviceCounts = {};
    requests.forEach(r => {
      const svc = r.service || 'Unknown';
      serviceCounts[svc] = (serviceCounts[svc] || 0) + 1;
    });
    const serviceBreakdown = Object.entries(serviceCounts).sort((a, b) => b[1] - a[1]);
    const topService = serviceBreakdown.length > 0 ? serviceBreakdown[0] : null;

    // City breakdown
    const cityCounts = {};
    requests.forEach(r => {
      const city = r.city || 'Unknown';
      cityCounts[city] = (cityCounts[city] || 0) + 1;
    });
    const cityBreakdown = Object.entries(cityCounts).sort((a, b) => b[1] - a[1]);

    // Technician workload
    const techCounts = {};
    requests.filter(r => r.assignedTo).forEach(r => {
      techCounts[r.assignedTo] = (techCounts[r.assignedTo] || 0) + 1;
    });
    const techBreakdown = Object.entries(techCounts).sort((a, b) => b[1] - a[1]);

    return {
      total, newCount, contacted, approved, dropped, completed,
      today, thisWeek, pending,
      serviceBreakdown, cityBreakdown, techBreakdown, topService
    };
  }, [requests]);

  const getFilterCount = (f) => {
    switch (f) {
      case 'new': return analytics.newCount;
      case 'contacted': return analytics.contacted;
      case 'approved': return analytics.approved;
      case 'dropped': return analytics.dropped;
      case 'completed': return analytics.completed;
      default: return null;
    }
  };

  // Filtered + searched requests
  const filteredRequests = useMemo(() => {
    let result = filter === 'all' ? requests : requests.filter(r => r.status === filter);
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter(r =>
        (r.name || '').toLowerCase().includes(term) ||
        (r.service || '').toLowerCase().includes(term) ||
        (r.city || '').toLowerCase().includes(term) ||
        (r.email || '').toLowerCase().includes(term) ||
        (r.phone || '').toLowerCase().includes(term) ||
        (r.assignedTo || '').toLowerCase().includes(term)
      );
    }
    return result;
  }, [requests, filter, searchTerm]);

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'new': return 'badge-new';
      case 'contacted': return 'badge-contacted';
      case 'approved': return 'badge-approved';
      case 'dropped': return 'badge-dropped';
      case 'completed': return 'badge-completed';
      default: return 'badge-default';
    }
  };

  const formatTime = (d) => d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  const formatDateShort = (d) => d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

  return (
    <div className="dashboard-container">
      {/* Header */}
      <motion.header
        className="dashboard-header"
        initial={{ opacity: 0, y: -12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <div className="header-left">
          <h1>MTS</h1>
          <p>Dashboard</p>
        </div>
        <div className="header-right">
          <div className="header-clock">
            <span className="clock-date">{formatDateShort(currentTime)}</span>
            <span className="clock-time">{formatTime(currentTime)}</span>
            <span className="clock-pulse" />
          </div>
          <button onClick={handleLogout} className="btn-logout">Sign Out</button>
        </div>
      </motion.header>

      {/* Stats */}
      <motion.div className="stats-row" variants={stagger} initial="hidden" animate="show">
        {[
          { value: analytics.total, label: 'Total Requests' },
          { value: analytics.pending, label: 'Pending' },
          { value: requests.filter(r => r.assignedTo).length, label: 'Assigned' },
          { value: analytics.today, label: 'Today' },
          { value: analytics.thisWeek, label: 'This Week' },
        ].map((stat, i) => (
          <motion.div key={i} className="stat-card" variants={fadeUp}>
            <div className="stat-value">{stat.value}</div>
            <div className="stat-label">{stat.label}</div>
          </motion.div>
        ))}
      </motion.div>

      {/* Breakdown Panels */}
      <motion.div className="breakdown-row" variants={stagger} initial="hidden" animate="show">
        {/* Service Breakdown */}
        <motion.div className="breakdown-panel" variants={fadeUp}>
          <div className="breakdown-title">Service Breakdown</div>
          {analytics.serviceBreakdown.length === 0 ? (
            <div className="breakdown-empty">No data</div>
          ) : (
            <div className="breakdown-list">
              {analytics.serviceBreakdown.map(([svc, count]) => (
                <div key={svc} className="breakdown-item">
                  <div className="breakdown-bar-wrap">
                    <div className="breakdown-name">{svc}</div>
                    <div className="breakdown-bar-track">
                      <motion.div
                        className="breakdown-bar-fill"
                        initial={{ width: 0 }}
                        animate={{ width: `${analytics.total > 0 ? (count / analytics.total) * 100 : 0}%` }}
                        transition={{ duration: 0.6, ease: 'easeOut' }}
                      />
                    </div>
                  </div>
                  <div className="breakdown-count">{count}</div>
                </div>
              ))}
            </div>
          )}
        </motion.div>

        {/* City Breakdown */}
        <motion.div className="breakdown-panel" variants={fadeUp}>
          <div className="breakdown-title">Requests by City</div>
          {analytics.cityBreakdown.length === 0 ? (
            <div className="breakdown-empty">No data</div>
          ) : (
            <div className="breakdown-list">
              {analytics.cityBreakdown.map(([city, count]) => (
                <div key={city} className="breakdown-item">
                  <div className="breakdown-bar-wrap">
                    <div className="breakdown-name">{city}</div>
                    <div className="breakdown-bar-track">
                      <motion.div
                        className="breakdown-bar-fill breakdown-bar-fill--city"
                        initial={{ width: 0 }}
                        animate={{ width: `${analytics.total > 0 ? (count / analytics.total) * 100 : 0}%` }}
                        transition={{ duration: 0.6, ease: 'easeOut' }}
                      />
                    </div>
                  </div>
                  <div className="breakdown-count">{count}</div>
                </div>
              ))}
            </div>
          )}
        </motion.div>

        {/* Quick Insights */}
        <motion.div className="breakdown-panel breakdown-panel--insights" variants={fadeUp}>
          <div className="breakdown-title">Quick Insights</div>
          <div className="insights-list">
            <div className="insight-row">
              <span className="insight-label">Top Service</span>
              <span className="insight-value">{analytics.topService ? analytics.topService[0] : '—'}</span>
            </div>
            <div className="insight-row">
              <span className="insight-label">Top City</span>
              <span className="insight-value">{analytics.cityBreakdown.length > 0 ? analytics.cityBreakdown[0][0] : '—'}</span>
            </div>
            <div className="insight-row">
              <span className="insight-label">Active Workload</span>
              <span className="insight-value insight-value--warn">{analytics.pending} pending</span>
            </div>
            <div className="insight-row">
              <span className="insight-label">Dropped</span>
              <span className={`insight-value ${analytics.dropped > 0 ? 'insight-value--dropped' : ''}`}>
                {analytics.dropped}
              </span>
            </div>
          </div>
        </motion.div>
      </motion.div>

      {/* Filter Bar + Search */}
      <div className="filter-bar">
        <div className="filter-buttons">
          {FILTER_OPTIONS.map(f => (
            <button
              key={f}
              className={filter === f ? 'filter-btn active' : 'filter-btn'}
              onClick={() => setFilter(f)}
            >
              {f}
              {f !== 'all' && (
                <span className="filter-count">{getFilterCount(f)}</span>
              )}
            </button>
          ))}
        </div>
        <div className="search-wrap">
          <svg className="search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
          </svg>
          <input
            type="text"
            className="search-input"
            placeholder="Search name, service, city, technician..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Requests */}
      <div className="requests-container">
        {loading ? (
          <div className="loading">
            <div className="loading-spinner" />
            Loading requests...
          </div>
        ) : filteredRequests.length === 0 ? (
          <div className="no-requests">
            {searchTerm ? `No results for "${searchTerm}"` : filter === 'all' ? 'No requests yet' : `No ${filter} requests`}
          </div>
        ) : (
          <motion.div className="requests-list" variants={stagger} initial="hidden" animate="show">
            <AnimatePresence mode="popLayout">
              {filteredRequests.map((request) => (
                <motion.div
                  key={request.id}
                  className={`request-card ${request.status === 'dropped' ? 'request-card--dropped' : request.status === 'new' ? 'request-card--new' : request.status === 'completed' ? 'request-card--completed' : ''}`}
                  variants={fadeUp}
                  layout
                  exit={{ opacity: 0, x: -20, transition: { duration: 0.2 } }}
                >
                  <div className="request-header">
                    <div className="request-title-row">
                      <h3>{request.name}</h3>
                      <span className={`status-badge ${getStatusBadgeClass(request.status)}`}>
                        <span className="badge-dot" />
                        {request.status || 'new'}
                      </span>
                    </div>
                    <div className="request-actions">
                      {/* Status */}
                      <div className="action-group">
                        <label className="action-label">Status</label>
                        <select
                          value={request.status || 'new'}
                          onChange={(e) => updateStatus(request.id, e.target.value)}
                          className="status-select"
                        >
                          {STATUS_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>

                      {/* Assign Technician */}
                      <div className="action-group">
                        <label className="action-label">Assign</label>
                        <select
                          value={request.assignedTo || ''}
                          onChange={(e) => updateField(request.id, 'assignedTo', e.target.value || null)}
                          className="status-select assign-select"
                        >
                          <option value="">Unassigned</option>
                          {TECHNICIANS.map(tech => (
                            <option key={tech} value={tech}>{tech}</option>
                          ))}
                        </select>
                      </div>

                      <button onClick={() => deleteRequest(request.id)} className="btn-delete" title="Delete request">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div className="request-details">
                    <div className="detail-row">
                      <strong>Service</strong> <span>{request.service}</span>
                    </div>
                    <div className="detail-row">
                      <strong>City</strong> <span>{request.city}</span>
                    </div>
                    {request.email && (
                      <div className="detail-row">
                        <strong>Email</strong>
                        <a href={`mailto:${request.email}`}>{request.email}</a>
                      </div>
                    )}
                    {request.phone && (
                      <div className="detail-row">
                        <strong>Phone</strong>
                        <a href={`tel:${request.phone}`}>{request.phone}</a>
                      </div>
                    )}
                    {request.assignedTo && (
                      <div className="detail-row">
                        <strong>Assigned To</strong>
                        <span className="assigned-name">{request.assignedTo}</span>
                      </div>
                    )}
                    <div className="detail-row timestamp">
                      <strong>Submitted</strong> <span>{formatDate(request.createdAt)}</span>
                    </div>
                  </div>

                  {/* Estimated Completion — only shows for approved requests */}
                  {request.status === 'approved' && (
                    <motion.div
                      className="estimated-completion"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <div className="est-label">Estimated Completion</div>
                      <input
                        type="datetime-local"
                        className="est-input"
                        value={request.estimatedCompletion || ''}
                        onChange={(e) => updateField(request.id, 'estimatedCompletion', e.target.value)}
                      />
                      {request.estimatedCompletion && (
                        <div className="est-display">
                          Target: {new Date(request.estimatedCompletion).toLocaleDateString('en-US', {
                            weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
                          })}
                        </div>
                      )}
                    </motion.div>
                  )}

                  {/* Dropped indicator */}
                  {request.status === 'dropped' && (
                    <div className="dropped-banner">
                      Project not approved — dropped
                    </div>
                  )}
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
